import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DemoDataService } from '@core/services/demo-data.service';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, UserClaims } from '@okta/okta-auth-js';
import {
  catchError,
  filter,
  from,
  map,
  Observable,
  of,
  throwError,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OktaIntegrationService {
  private _oktaAuth = inject(OKTA_AUTH);
  private _router = inject(Router);
  private _oktaAuthStateService = inject(OktaAuthStateService);
  private _demoDataService = inject(DemoDataService);
  private _demoUserLoggedIn = false;
  private OKTA_ORIGINAL_URI_KEY = 'okta_original_uri';

  constructor() {}

  public getIsAuthenticated(): Observable<boolean> {
    if (this._demoDataService.isDemoActive()) {
      return of(this._demoUserLoggedIn);
    }
    return this._oktaAuthStateService.authState$.pipe(
      filter((s: AuthState) => !!s),
      map((s: AuthState) => s.isAuthenticated ?? false)
    );
  }

  /**
   * Function that will execute the redirection to the okta hosted page to proceed to the user identification.
   * @param {string} origin - page where the client who presses the `login` button is located
   * @param {string} navigateTo - page previous to redirect
   */
  public signInWithRedirect(
    origin: string,
    navigateTo: string
  ): Observable<void> {
    if (this._demoDataService.isDemoActive()) {
      this._demoUserLoggedIn = true;
      return of();
    }

    return from(
      this._oktaAuth.signInWithRedirect({ originalUri: origin })
    ).pipe(
      map(() => {
        void this._router.navigate([navigateTo], {
          queryParamsHandling: 'preserve',
        });
      }),
      catchError((error: unknown) => {
        console.error('Okta sign in error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout function, remove all information okta
   * @param {string} origin
   */
  public signOut(): Observable<void> {
    if (this._demoDataService.isDemoActive()) {
      this._demoUserLoggedIn = false;
      return of();
    }

    return from(this._oktaAuth.signOut()).pipe(
      map(() => {
        const checkoutUrl = location.pathname;
        const urlParameters = new URLSearchParams(globalThis.location.search);
        const token = urlParameters.get('token');
        const checkoutUrlData = JSON.stringify({
          path: checkoutUrl,
          token,
          timestamp: Date.now(),
        });
        localStorage.setItem('checkout-url', checkoutUrlData);
      }),
      catchError((error: unknown) => {
        console.error('Okta sign out error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Return Patron info from Okta
   * @returns Promise<UserClaims>
   */
  public async getUserInfo(): Promise<UserClaims> {
    if (this._demoDataService.isDemoActive() && this._demoUserLoggedIn) {
      return {
        sub: 'demo-user-123',
        email: 'demo@example.com',
        email_verified: true,
        name: 'Demo User',
        given_name: 'Demo',
        family_name: 'User',
        preferred_username: 'demo@example.com',
        accountCredit: 100_000,
      } as UserClaims;
    }
    return await this._oktaAuth.getUser();
  }

  public getAccessToken(): string {
    if (this._demoDataService.isDemoActive() && this._demoUserLoggedIn) {
      return 'demo-access-token';
    }
    return this._oktaAuth.getAccessToken()!;
  }
}
